import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { auth as authApi } from '@/api/endpoints';
import { clearToken, loadToken, saveToken } from '@/api/client';
import type { CurrentUser, RegisterRequest } from '@/types/api';

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (body: RegisterRequest) => Promise<void>;
  logout: () => void;
  /** Replaces the in-memory user, e.g. after a profile update, without a network round-trip. */
  updateUser: (user: CurrentUser) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const token = loadToken();
    if (!token) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- no token: nothing to fetch, finish loading
      setLoading(false);
      return;
    }
    authApi
      .me()
      .then(setUser)
      .catch(() => {
        clearToken();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password);
    saveToken(res.token);
    const me = await authApi.me();
    setUser(me);
  }, []);

  // Registration auto-signs the user in, mirroring the login flow.
  const register = useCallback(async (body: RegisterRequest) => {
    const res = await authApi.register(body);
    saveToken(res.token);
    const me = await authApi.me();
    setUser(me);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    // Every query (stats, items, locations, ...) is keyed by endpoint, not by
    // user -- without this, whatever the next account logs in and lands on
    // gets the previous account's cached response rendered instantly, before
    // the real refetch for the new session completes.
    queryClient.clear();
  }, [queryClient]);

  const updateUser = useCallback((next: CurrentUser) => {
    setUser(next);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, updateUser }),
    [user, loading, login, register, logout, updateUser],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
